import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";

const CATEGORIES = [
  {
    num: "01",
    title: "Coaching & Development",
    intro:
      "One-on-one and cohort development for writers, speakers, artists, and advocates ready to deepen their craft and grow their platforms.",
    services: [
      "Individual and professional coaching",
      "Story and platform development",
      "Speaker development",
      "Creative and professional growth",
    ],
    focus: "Coaching — Speaker / Artist / Business Development",
    testId: "work-coaching",
  },
  {
    num: "02",
    title: "Consulting & Business Development",
    intro:
      "Strategic counsel for mission-led people and cultural institutions building durable, values-aligned work.",
    services: [
      "Strategic consulting",
      "Business development",
      "Client development",
      "Opportunity development",
      "Program and partnership development",
    ],
    focus: "Consulting & Business Development",
    testId: "work-consulting",
  },
  {
    num: "03",
    title: "Boutique Talent Management",
    intro:
      "Selective, hands-on representation for distinct voices — speakers, performers, and survivor-centered artists.",
    services: [
      "Talent representation",
      "Booking development",
      "Relationship management",
      "Professional positioning",
      "Career and opportunity development",
    ],
    focus: "Book Featured Talent",
    testId: "work-talent",
  },
  {
    num: "04",
    title: "Speaking, Workshops & Facilitation",
    intro:
      "Programs for stages, classrooms, boardrooms, and community spaces — designed with and for your audience.",
    services: [
      "Keynotes",
      "Workshops",
      "Training",
      "Retreats",
      "Facilitated conversations",
    ],
    focus: "Book Featured Talent",
    testId: "work-speaking",
  },
];

const OurWorkPage = () => {
  return (
    <>
      <Seo
        title="Our Work — #whatshesaidproject"
        description="Coaching & development, consulting & business development, boutique talent management, and speaking, workshops & facilitation — mission-led creative work."
        path="/our-work"
      />
      {/* HERO */}
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
            Our Work
          </span>
          <h1
            data-testid="our-work-headline"
            className="font-disp text-[clamp(36px,5vw,72px)] leading-[1.02] tracking-tight font-normal text-[#121212] text-balance max-w-4xl mb-6"
          >
            How we collaborate.
          </h1>
          <p className="text-[17px] leading-[1.65] text-[#555555] max-w-2xl">
            Four practices, one purpose: helping mission-led people develop their work, strengthen
            their platforms, and create meaningful connection and impact.
          </p>
        </div>
      </section>

      {/* SERVICE CATEGORIES */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {CATEGORIES.map((cat) => (
              <article
                key={cat.num}
                data-testid={`work-card-${cat.testId}`}
                className="bg-white border border-[#e5e5e5] hover:border-[#121212] transition-colors p-8 md:p-10 flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-mono uppercase tracking-[.14em] text-[#e02020] block mb-4">
                    Practice {cat.num}
                  </span>
                  <h2 className="font-disp text-3xl text-[#121212] mb-4">{cat.title}</h2>
                  <p className="text-sm text-[#555555] leading-relaxed mb-8">{cat.intro}</p>
                  <ul className="space-y-3 mb-8">
                    {cat.services.map((service) => (
                      <li
                        key={service}
                        className="flex items-center gap-3 text-xs font-mono uppercase tracking-[.14em] text-[#444444]"
                      >
                        <span className="w-2 h-2 bg-[#e02020]" aria-hidden="true" />
                        <span>{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={`/connect?focus=${encodeURIComponent(cat.focus)}`}
                  data-testid={`${cat.testId}-inquiry-link`}
                  className="inline-flex items-center text-xs font-mono uppercase tracking-[.14em] text-[#121212] font-semibold underline underline-offset-4 hover:text-[#e02020] transition-colors"
                >
                  Start an Inquiry →
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#121212] text-white py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Not sure where to begin?
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-white mb-6">
                Tell us where you are headed.
              </h2>
              <p className="text-[17px] text-[#aaaaaa] leading-relaxed max-w-2xl">
                Send a general inquiry and we will route it to the right practice — or recommend
                the right voice from our roster.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/connect"
                data-testid="our-work-cta-connect"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-white hover:text-[#121212] transition-colors rounded-none"
              >
                Start an Inquiry →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default OurWorkPage;
